import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FeedService } from '../../services/feedService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { useFeedWatchlist } from '../../hooks/useFeedWatchlist';
import { HOME_FEED_TABS, feedCardAnalytics, mergeFeedCards } from '@aluvo/constants/src/homeFeed';
import { CoinActivityCard, buildMyTierMap } from '../ActivityFeed/ActivityFeed';
import {
    EditorialCollectionCard, EditorialListCard, EditorialTitleCard, RankingCard, RatingGroupCard, RecommendationRail,
} from './FeedCards';
import styles from './HomeFeed.module.css';

// Home feed: For You (default) and Following (docs/home-feed.md).

const TABS = [
    { id: HOME_FEED_TABS.FOR_YOU, label: 'For You' },
    { id: HOME_FEED_TABS.FOLLOWING, label: 'Following' },
];
const EMPTY_TAB = { cards: [], cursor: null, hasMore: false, meta: null, status: 'idle', loadingMore: false, moreError: false };

/** Fires `onVisible` once, when at least half of the element has been on screen. */
function Impression({ id, onVisible, children }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') return undefined;
        const observer = new window.IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) {
                onVisible(id);
                observer.disconnect();
            }
        }, { threshold: [0.5] });
        observer.observe(el);
        return () => observer.disconnect();
    }, [id, onVisible]);
    return <div ref={ref}>{children}</div>;
}

function Skeleton() {
    return (
        <div aria-busy="true" aria-label="Loading your feed">
            {[1, 2, 3].map((i) => <div key={i} className={styles.skeleton} />)}
        </div>
    );
}

export default function HomeFeed({ myCoins = [], onMeta }) {
    const navigate = useNavigate();
    const [tab, setTab] = useState(HOME_FEED_TABS.FOR_YOU);
    const [tabs, setTabs] = useState({ [HOME_FEED_TABS.FOR_YOU]: EMPTY_TAB, [HOME_FEED_TABS.FOLLOWING]: EMPTY_TAB });
    // Card ids already counted as impressions, per tab load (reset on reload).
    const seenImpressions = useRef({ [HOME_FEED_TABS.FOR_YOU]: new Set(), [HOME_FEED_TABS.FOLLOWING]: new Set() });
    const requestSeq = useRef({ [HOME_FEED_TABS.FOR_YOU]: 0, [HOME_FEED_TABS.FOLLOWING]: 0 });
    const myTierMap = useMemo(() => buildMyTierMap(myCoins), [myCoins]);
    const current = tabs[tab];

    const patch = useCallback((id, changes) => {
        setTabs((prev) => ({ ...prev, [id]: { ...prev[id], ...changes } }));
    }, []);

    const load = useCallback(async (id) => {
        const seq = ++requestSeq.current[id];
        seenImpressions.current[id] = new Set();
        patch(id, { ...EMPTY_TAB, status: 'loading' });
        try {
            const data = await FeedService.getHome(id);
            if (seq !== requestSeq.current[id]) return;
            patch(id, { cards: mergeFeedCards([], data?.cards), cursor: data?.nextCursor ?? null, hasMore: !!data?.hasMore, meta: data?.meta ?? null, status: 'ready' });
            if (id === HOME_FEED_TABS.FOR_YOU) onMeta?.(data?.meta ?? null);
        } catch {
            if (seq === requestSeq.current[id]) patch(id, { status: 'error' });
        }
    }, [patch, onMeta]);

    const loadMore = useCallback(async () => {
        const id = tab;
        const state = tabs[id];
        if (!state.cursor || state.loadingMore) return;
        const seq = requestSeq.current[id];
        patch(id, { loadingMore: true, moreError: false });
        try {
            const data = await FeedService.getHome(id, state.cursor);
            if (seq !== requestSeq.current[id]) return;
            setTabs((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    cards: mergeFeedCards(prev[id].cards, data?.cards),
                    cursor: data?.nextCursor ?? null,
                    hasMore: !!data?.hasMore,
                    loadingMore: false,
                },
            }));
        } catch {
            if (seq === requestSeq.current[id]) patch(id, { loadingMore: false, moreError: true });
        }
    }, [tab, tabs, patch]);

    useEffect(() => { load(HOME_FEED_TABS.FOR_YOU); }, [load]);

    function selectTab(id) {
        if (id === tab) return;
        trackEvent(ANALYTICS_EVENTS.HOME_FEED_TAB_SELECTED, { tab: id, previous_tab: tab });
        setTab(id);
        if (tabs[id].status === 'idle' || tabs[id].status === 'error') load(id);
    }

    const positionOf = useCallback((card) => tabs[tab].cards.findIndex((c) => c.id === card.id), [tabs, tab]);
    const params = useCallback((card) => feedCardAnalytics(card, { tab, position: positionOf(card) }), [tab, positionOf]);

    const onImpression = useCallback((cardId) => {
        const seen = seenImpressions.current[tab];
        if (seen.has(cardId)) return;
        seen.add(cardId);
        const card = tabs[tab].cards.find((c) => c.id === cardId);
        if (card) trackEvent(ANALYTICS_EVENTS.HOME_FEED_CARD_IMPRESSION, params(card));
    }, [tab, tabs, params]);

    const watchlist = useFeedWatchlist({
        onAdded: (title, card) => trackEvent(ANALYTICS_EVENTS.HOME_FEED_WATCHLIST_ADDED, { ...(card ? params(card) : { tab }), media_type: title.kind }),
    });

    const openTitle = (card) => (title) => {
        trackEvent(ANALYTICS_EVENTS.HOME_FEED_TITLE_OPENED, { ...params(card), media_type: title.kind });
        navigate(`/movie/${title.kind}/${title.tmdbId}`);
    };
    const openRecommendation = (card, title) => {
        trackEvent(ANALYTICS_EVENTS.HOME_FEED_RECOMMENDATION_CLICKED, { ...params(card), media_type: title.kind });
        navigate(`/movie/${title.kind}/${title.tmdbId}`);
    };
    const openRanking = (card) => {
        trackEvent(ANALYTICS_EVENTS.HOME_FEED_RANKING_OPENED, params(card));
        navigate(`/rankings/${encodeURIComponent(card.ranking.slug)}`);
    };
    const openEditorial = (card, path) => {
        trackEvent(ANALYTICS_EVENTS.HOME_FEED_EDITORIAL_OPENED, params(card));
        navigate(path);
    };
    const saveFrom = (card) => (title) => watchlist.save(title, card);

    function renderCard(card) {
        switch (card.type) {
            case 'ranking':
                return <RankingCard card={card} statusOf={watchlist.statusOf} onOpenRanking={openRanking} onOpenTitle={openTitle(card)} onSave={saveFrom(card)} />;
            case 'editorial_title':
                return <EditorialTitleCard card={card} statusOf={watchlist.statusOf} onOpenTitle={openTitle(card)} onSave={saveFrom(card)} />;
            case 'editorial_collection':
                return <EditorialCollectionCard card={card} onOpenEditorial={openEditorial} />;
            case 'editorial_list':
                return <EditorialListCard card={card} onOpenEditorial={openEditorial} />;
            case 'recommendations':
                return <RecommendationRail card={card} statusOf={watchlist.statusOf} onOpenRecommendation={openRecommendation} onSave={saveFrom(card)} />;
            case 'rating_group':
                return <RatingGroupCard card={card} onOpenTitle={openTitle(card)} />;
            case 'coin':
                return (
                    <CoinActivityCard
                        event={card.event}
                        myTier={myTierMap[String(card.event.coin?.tmdbId ?? '')] ?? null}
                        onTryIt={(media) => navigate(`/movie/${media.kind}/${media.tmdbId}`)}
                        onUserClick={(username) => navigate(`/user/${username}`)}
                        isLoggedIn
                    />
                );
            default:
                return null; // a card type this build doesn't know yet
        }
    }

    function renderEmpty() {
        if (tab === HOME_FEED_TABS.FOLLOWING) {
            const followsNobody = !current.meta?.followingCount;
            return (
                <div className={styles.empty}>
                    <h3>{followsNobody ? 'Follow people to fill this tab' : 'No activity yet from people you follow'}</h3>
                    <p>
                        {followsNobody
                            ? 'Their published rankings, movie ratings and coins will show up here. For You keeps working in the meantime.'
                            : 'When they publish a ranking, rate a movie or earn a coin, it will show up here.'}
                    </p>
                    <Link
                        to="/discover?tab=people"
                        className={styles.primaryBtn}
                        onClick={() => trackEvent(ANALYTICS_EVENTS.HOME_FEED_DISCOVER_PEOPLE_CLICKED, { tab, following_count: current.meta?.followingCount ?? 0 })}
                    >
                        Find people to follow
                    </Link>
                </div>
            );
        }
        return (
            <div className={styles.empty}>
                <h3>Nothing to show yet</h3>
                <p>Explore movies and shows, take a challenge, or start a collection.</p>
                <div className={styles.emptyActions}>
                    <Link to="/discover" className={styles.primaryBtn}>Discover</Link>
                    <Link to="/collections" className={styles.secondaryLink}>Collections</Link>
                    <Link to="/rankings/new" className={styles.secondaryLink}>Make a ranking</Link>
                </div>
            </div>
        );
    }

    return (
        <section className={styles.feed} aria-label="Home feed">
            <div className={styles.tabs} role="tablist" aria-label="Feed">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        id={`home-feed-tab-${t.id}`}
                        aria-selected={tab === t.id}
                        aria-controls="home-feed-panel"
                        className={tab === t.id ? styles.tabActive : styles.tab}
                        onClick={() => selectTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div id="home-feed-panel" role="tabpanel" aria-labelledby={`home-feed-tab-${tab}`}>
                {(current.status === 'loading' || current.status === 'idle') && <Skeleton />}
                {current.status === 'error' && (
                    <div className={styles.empty} role="alert">
                        <h3>Couldn&apos;t load your feed</h3>
                        <p>Check your connection and try again.</p>
                        <button type="button" className={styles.primaryBtn} onClick={() => load(tab)}>Try again</button>
                    </div>
                )}
                {current.status === 'ready' && current.cards.length === 0 && renderEmpty()}
                {current.status === 'ready' && current.cards.length > 0 && (
                    <>
                        <ol className={styles.cards}>
                            {current.cards.map((card) => (
                                <li key={card.id}>
                                    <Impression id={card.id} onVisible={onImpression}>{renderCard(card)}</Impression>
                                </li>
                            ))}
                        </ol>
                        {current.hasMore && (
                            <div className={styles.more}>
                                {current.moreError && <p className={styles.saveError} role="alert">Couldn&apos;t load more.</p>}
                                <button type="button" className={styles.secondaryBtn} onClick={loadMore} disabled={current.loadingMore}>
                                    {current.loadingMore ? 'Loading…' : current.moreError ? 'Try again' : 'Show more'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}
